import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { HistoryFilters } from '../types'
import type { HistoryTab } from '../api/history.api'

/**
 * Persisted UI snapshot for /workspace/history. Mirrors the queue's
 * snapshot model: filter/sort/page selections survive across detail-page
 * navigation so the coder lands back on the SAME row set after viewing an
 * encounter.
 *
 * Persisted to sessionStorage (per-tab), same as `useQueueContextStore` — a
 * hard refresh on /workspace/history restores the coder's filters instead of
 * silently snapping back to the default "last 7 days, all statuses" view and
 * refetching a different row set than the one they were working.
 *
 * Lives in its own store (rather than `useQueueContextStore.snapshot`) so the
 * queue's snapshot stays specific to the queue page's shape (cursorStack,
 * viewMode, QueryBucketFilter), and history's specific to its own shape.
 */
export interface HistorySnapshot {
  tab: HistoryTab
  filters: Partial<HistoryFilters>
  searchQuery: string
  pageSize: number
  cursor: string | null
  pageIndex: number
  /** Prev-walk stack so back-nav restores the Prev button history. */
  cursorStack: (string | undefined)[]
  visibleColumns: string[]
  columnOrder: string[]
}

interface HistoryContextState {
  snapshot: HistorySnapshot | null
  setSnapshot: (snapshot: HistorySnapshot) => void
  clear: () => void
}

export const useHistoryContextStore = create<HistoryContextState>()(
  persist(
    (set) => ({
      snapshot: null,
      setSnapshot: (snapshot) => set({ snapshot }),
      clear: () => set({ snapshot: null }),
    }),
    {
      name: 'workspace-history-context',
      storage: createJSONStorage(() => sessionStorage),
      // Only the snapshot is durable; setters are rebuilt by the factory on
      // every load. sessionStorage is synchronous, so the store is already
      // rehydrated by the time history.tsx reads getState().snapshot during
      // its first render — no hydration race, same as the queue store.
      partialize: (state) => ({ snapshot: state.snapshot }),
    },
  ),
)
